import type { ChangeEvent } from 'react';
import { Calendar } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import type { QuestionRendererProps } from '../types';

export const DateQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
}: QuestionRendererProps<string>) => {
  if (question.type !== 'date') {
    return null;
  }

  const resolveIsoDate = (value?: string) => {
    if (!value) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed.toISOString().split('T')[0];
  };

  const formatPlaceholder = () => {
    switch (question.dateFormat) {
      case 'dd/MM/yyyy':
        return 'DD/MM/YYYY';
      case 'yyyy-MM-dd':
        return 'YYYY-MM-DD';
      default:
        return 'MM/DD/YYYY';
    }
  };

  const isInteractive = mode === 'runtime' && !disabled;
  const placeholder = question.placeholder || formatPlaceholder();

  // Parse date constraints if provided
  const minDate = resolveIsoDate(question.minDate);
  const maxDate = resolveIsoDate(question.maxDate);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isInteractive) {
      return;
    }
    // Store as ISO date string (YYYY-MM-DD)
    onChange?.(event.target.value);
  };

  const handleTodayClick = () => {
    if (!isInteractive) {
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    onChange?.(today);
    onBlur?.();
  };

  // Format value for display based on dateFormat preference
  const displayValue = resolveIsoDate(value) ?? '';

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type="date"
            value={displayValue}
            onChange={handleChange}
            onBlur={() => onBlur?.()}
            placeholder={placeholder}
            disabled={!isInteractive}
            min={minDate}
            max={maxDate}
            aria-invalid={Boolean(error)}
            aria-describedby={`validation-${question.id}`}
            aria-errormessage={error ? `error-${question.id}` : undefined}
            className="pr-10"
          />
          <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        {isInteractive && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleTodayClick}
            className="whitespace-nowrap"
          >
            Today
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {minDate && maxDate
          ? `Select a date between ${minDate} and ${maxDate}`
          : minDate
            ? `Select a date on or after ${minDate}`
            : maxDate
              ? `Select a date on or before ${maxDate}`
              : 'Select a date'}
      </p>
    </div>
  );
};
